import * as React from "react";
import { Route, Routes, Navigate, Outlet, useSearchParams, useLocation } from "react-router-dom";
import { SiteHeader } from "./components/site-header.tsx";
import { SiteFooter } from "./components/site-footer.tsx";
import { useLocale } from "./lib/locale-provider.tsx";
import { useSession } from "./lib/auth-client.ts";
import { LocaleProvider } from "./lib/locale-provider.tsx";
import { CookieBanner } from "./components/cookie-banner.tsx";

// Lazy-load every page → bundle stays small per Section 10.1.
const Landing = React.lazy(() => import("./pages/landing.tsx"));
const Pricing = React.lazy(() => import("./pages/pricing.tsx"));
const About = React.lazy(() => import("./pages/about.tsx"));
const Terms = React.lazy(() => import("./pages/terms.tsx"));
const Privacy = React.lazy(() => import("./pages/privacy.tsx"));
const Cookies = React.lazy(() => import("./pages/cookies.tsx"));
const Login = React.lazy(() => import("./pages/login.tsx"));
const Signup = React.lazy(() => import("./pages/signup.tsx"));
const ForgotPassword = React.lazy(() => import("./pages/forgot-password.tsx"));
const ResetPassword = React.lazy(() => import("./pages/reset-password.tsx"));
const VerifyEmail = React.lazy(() => import("./pages/verify-email.tsx"));
const Browse = React.lazy(() => import("./pages/browse.tsx"));
const SeriesDetail = React.lazy(() => import("./pages/series-detail.tsx"));
const Watch = React.lazy(() => import("./pages/watch.tsx"));
const Search = React.lazy(() => import("./pages/search.tsx"));
const MyList = React.lazy(() => import("./pages/my-list.tsx"));
const Subscribe = React.lazy(() => import("./pages/subscribe.tsx"));
const CheckoutSuccess = React.lazy(() => import("./pages/checkout-success.tsx"));
const CheckoutCancel = React.lazy(() => import("./pages/checkout-cancel.tsx"));
const Settings = React.lazy(() => import("./pages/settings.tsx"));
const AccountDeleted = React.lazy(() => import("./pages/account-deleted.tsx"));
const NotFound = React.lazy(() => import("./pages/not-found.tsx"));

export function App() {
  return (
    <LocaleProvider>
      <Shell />
    </LocaleProvider>
  );
}

function Shell() {
  const { locale } = useLocale();
  const location = useLocation();
  // Netflix-style: the watch route is a full-bleed cinema view. Hide the
  // site chrome (header/footer/cookie banner) so the player owns the screen.
  const cinema = location.pathname.startsWith("/watch/");
  return (
    <>
      {!cinema && <SiteHeader locale={locale} />}
      <React.Suspense fallback={<PageFallback />}>
        <main id="main" className={cinema ? "" : "pt-16"}>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route element={<RedirectIfAuthed />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/account-deleted" element={<AccountDeleted />} />

            <Route element={<RequireAuth />}>
              <Route path="/browse" element={<Browse />} />
              <Route path="/series/:slug" element={<SeriesDetail />} />
              <Route path="/search" element={<Search />} />
              <Route path="/my-list" element={<MyList />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/settings/*" element={<Settings />} />
            </Route>

            <Route element={<RequireSubscription />}>
              <Route path="/watch/:seriesId/:episodeId" element={<Watch />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </React.Suspense>
      {!cinema && <SiteFooter locale={locale} />}
      {!cinema && <CookieBanner />}
    </>
  );
}

function RequireAuth() {
  const { data, isPending } = useSession();
  if (isPending) return <PageFallback />;
  if (!data) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

function HomeRoute() {
  // Netflix-style: signed-in users skip the marketing landing and go
  // straight to the catalogue. We don't gate on subscription here — the
  // browse rows themselves degrade gracefully if a user isn't subscribed
  // (CTAs route to /pricing from inside).
  const { data, isPending } = useSession();
  if (isPending) return <PageFallback />;
  if (data) return <Navigate to="/browse" replace />;
  return <Landing />;
}

function RedirectIfAuthed() {
  const { data, isPending } = useSession();
  const [params] = useSearchParams();
  if (isPending) return <PageFallback />;
  if (data) {
    const next = params.get("next");
    return <Navigate to={next && next.startsWith("/") ? next : "/browse"} replace />;
  }
  return <Outlet />;
}

function RequireSubscription() {
  const { data, isPending } = useSession();
  if (isPending) return <PageFallback />;
  if (!data) {
    const next = encodeURIComponent(window.location.pathname);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  // TODO: when /api/billing/subscription returns status:"active|trialing", gate here too.
  return <Outlet />;
}

function PageFallback() {
  return (
    <div className="grid min-h-[60dvh] place-items-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-muted)] border-t-[var(--color-accent)]"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}
